import React, { useEffect, useState } from "react";
import {
  Button, Skeleton, Modal, ModalOverlay, ModalContent,
  ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
} from "@chakra-ui/react";
import { useSelector } from "react-redux";
import { FaFutbol, FaTrophy, FaCalendarAlt, FaChevronRight } from "react-icons/fa";
import Layout from "../../Components/Layout";
import { usePlay } from "./usePlayer";
import { RootState } from "../../Store/types";
import { getRecords, PlayerStats } from "../../Services/home";
import "../../Components/Layout/football-details.css";
import "./profile.css";

export const Player: React.FC = () => {
  const { handleGetPlay, loading } = usePlay();
  const { goal_count, total_goals, user } = useSelector((state: RootState) => state.user);
  const seasonId = useSelector((state: RootState) => state.season.data?.id);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [recordData, setRecordData] = useState<PlayerStats>();
  const [recordsLoading, setRecordsLoading] = useState(true);
  const [recordsError, setRecordsError] = useState(false);
  const [retry, setRetry] = useState(0);
  const name = user?.name || "Jogador";
  const words = name.trim().split(/\s+/);
  const initials = (words[0].charAt(0) + (words.length > 1 ? words[words.length - 1].charAt(0) : "")).toUpperCase();

  useEffect(() => {
    if (user?.id && seasonId) handleGetPlay(user.id, seasonId);
  }, [handleGetPlay, user?.id, seasonId]);

  useEffect(() => {
    if (!user?.id) return;
    let active = true;
    setRecordsLoading(true);
    setRecordsError(false);
    setRecordData(undefined);
    getRecords(user.id).then((response) => {
      if (!active) return;
      setRecordData(response);
      setRecordsError(!response);
    }).catch(() => {
      if (active) setRecordsError(true);
    }).finally(() => {
      if (active) setRecordsLoading(false);
    });
    return () => { active = false; };
  }, [user?.id, retry]);

  const closeModal = () => setIsModalOpen(false);

  return (
    <Layout title="Suas informações">
      <div className="player-profile">
        <section className="profile-hero" aria-label="Perfil do jogador">
          <span className="football-eyebrow">MEU FUTEBOL</span>
          <div className="profile-crest" aria-hidden="true"><FaFutbol /><strong>{initials}</strong></div>
          <h1>{name}</h1>
          <p>Seu desempenho dentro de campo.</p>
        </section>

        <section className="profile-numbers" aria-label="Seu desempenho" aria-busy={loading || recordsLoading}>
          <div className="profile-stat profile-stat--goals">
            <span className="profile-stat__icon"><FaFutbol aria-hidden="true" /></span>
            <div><span>Gols marcados</span>{loading ? <Skeleton height="42px" width="80px" /> : <strong>{total_goals ?? "—"}</strong>}</div>
            <span className="profile-stat__tag">NA TEMPORADA</span>
          </div>
          <div className="profile-stat">
            <FaCalendarAlt aria-hidden="true" />
            {loading ? <Skeleton height="34px" width="60px" /> : <strong>{goal_count ?? "—"}</strong>}
            <span>Partidas jogadas</span>
            <small>Na temporada</small>
          </div>
          <div className="profile-stat profile-stat--against">
            <FaFutbol aria-hidden="true" />
            {recordsLoading ? <Skeleton height="34px" width="60px" /> : <strong>{recordData?.total_own_goals ?? "—"}</strong>}
            <span>Gols contra</span>
            <small>No histórico</small>
          </div>
        </section>
        {recordsError && <div className="profile-error" role="alert">Não foi possível carregar as estatísticas. <Button variant="unstyled" onClick={() => setRetry((value) => value + 1)}>Tentar novamente</Button></div>}
        <button className="profile-records" onClick={() => setIsModalOpen(true)}>
          <FaTrophy aria-hidden="true" />
          <span><strong>Meus recordes</strong><small>Os números da sua trajetória</small></span>
          <FaChevronRight aria-hidden="true" />
        </button>
      </div>

      <Modal isOpen={isModalOpen} onClose={closeModal} isCentered scrollBehavior="inside">
        <ModalOverlay bg="blackAlpha.800" />
        <ModalContent className="football-modal" mx={4} maxW="420px">
          <ModalHeader className="football-modal__header">
            <span className="football-eyebrow"><FaTrophy aria-hidden="true" /> MEUS RECORDES</span>
            <span className="football-modal__name">{name}</span>
          </ModalHeader>
          <ModalCloseButton className="football-modal__close" aria-label="Fechar" />
          <ModalBody className="football-modal__body">
            {recordsLoading ? <Skeleton height="160px" borderRadius="12px" /> : recordsError ? (
              <div className="profile-error" role="alert">Estatísticas indisponíveis. <Button variant="unstyled" onClick={() => setRetry((value) => value + 1)}>Tentar novamente</Button></div>
            ) : (
              <dl className="profile-record-list">
                <div><dt>Máximo de gols</dt><dd>{recordData?.max_goals ?? "—"}</dd></div>
                <div><dt>Média de gols</dt><dd>{recordData?.average_goals ?? "—"}</dd></div>
                <div><dt>Partidas jogadas</dt><dd>{recordData?.matches_played ?? "—"}</dd></div>
              </dl>
            )}
          </ModalBody>
          <ModalFooter className="football-modal__footer">
            <Button variant="unstyled" className="football-save" w="full" onClick={closeModal}>Voltar ao perfil</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Layout>
  );
};
